"use client";
import React, { useState, useEffect } from "react";
import Link from "next/link";
import Image from "next/image";

import { Shield } from "lucide-react";

import { useT } from "@/context/I18nContext";
import { userService } from "@/services/user.service";
import { fetchWrapper } from "@/helpers/fetch-wrapper";

function tenureLabel(createdAt) {
	if (!createdAt) return null;
	const days = Math.max(0, Math.floor((Date.now() - new Date(createdAt).getTime()) / 86400000));
	if (days < 30) return `Client de ${days} ${days === 1 ? "zi" : "zile"}`;
	const months = Math.floor(days / 30);
	if (months < 12) return `Client de ${months} ${months === 1 ? "lună" : "luni"}`;
	const years = Math.floor(months / 12);
	return `Client de ${years} ${years === 1 ? "an" : "ani"}`;
}

export default function MobileMenu() {
	const [navigationStack, setNavigationStack] = useState([]);
	const [loading, setLoading] = useState(true);
	const [childrenCache, setChildrenCache] = useState({});
	const [expandedCategories, setExpandedCategories] = useState(new Set());
	const [userData, setUserData] = useState(null);
	const [userState, setUserState] = useState(false);
	const [topOffset, setTopOffset] = useState(0);

	const { t } = useT();

	useEffect(() => {
		const header = document.getElementById("header");
		if (!header) return;

		const measure = () => setTopOffset(Math.max(0, header.getBoundingClientRect().bottom));
		measure();

		const ro = new ResizeObserver(measure);
		ro.observe(header);
		window.addEventListener("resize", measure);
		window.addEventListener("scroll", measure, { passive: true });

		return () => {
			ro.disconnect();
			window.removeEventListener("resize", measure);
			window.removeEventListener("scroll", measure);
		};
	}, []);

	useEffect(() => {
		fetchMainCategories();
	}, []);

	useEffect(() => {
		let alive = true;
		const safe = (fn) => alive && fn();

		const fetchUserData = async () => {
			try {
				const u = userService.userValue;
				if (!u?.token) {
					safe(() => {
						setUserData(null);
						setUserState(false);
					});
					return;
				}
				const response = await fetchWrapper.get(`/api/users/getdata`);
				safe(() => {
					setUserData(response?.[0]?.userData ?? null);
					setUserState(true);
				});
			} catch (e) {
				safe(() => {
					setUserData(null);
					setUserState(false);
				});
			}
		};

		fetchUserData();
		const sub = userService.user.subscribe(() => fetchUserData());

		return () => {
			alive = false;
			sub.unsubscribe();
		};
	}, []);

	const fetchMainCategories = async () => {
		try {
			setLoading(true);
			const response = await fetch("/api/categories/main");
			const data = await response.json();
			setNavigationStack([
				{
					categories: data.categories || [],
					title: "Contul meu",
					level: 0,
					breadcrumb: [],
				},
			]);
		} catch (error) {
			console.error("Error fetching main categories:", error);
		} finally {
			setLoading(false);
		}
	};

	const fetchChildren = async (categoryId) => {
		if (childrenCache[categoryId]) {
			return childrenCache[categoryId];
		}

		try {
			const response = await fetch(`/api/categories/${categoryId}/children`);
			const data = await response.json();
			const children = data.children || [];

			setChildrenCache((prev) => ({ ...prev, [categoryId]: children }));
			return children;
		} catch (error) {
			console.error("Error fetching children:", error);
			return [];
		}
	};

	const toggleDropdown = (categoryId) => {
		setExpandedCategories((prev) => {
			const newSet = new Set(prev);
			if (newSet.has(categoryId)) {
				newSet.delete(categoryId);
			} else {
				newSet.add(categoryId);
			}
			return newSet;
		});
	};

	const buildCategoryUrl = (breadcrumb, currentSlug) => {
		const slugs = [...breadcrumb.map((b) => b.slug), currentSlug];
		return `/${slugs.join("/")}`;
	};

	const categoryName = (category) => {
		const variant = t("variable_categorii");
		if (variant === "name_en") return category.name_en || category.name;
		if (variant === "name_it") return category.name_it || category.name;
		if (variant === "name_fr") return category.name_fr || category.name;
		if (variant === "name_de") return category.name_de || category.name;
		return category.name;
	};

	const formatProductCount = (count) => {
		if (!count || count === 0) return null;
		if (count >= 1000) {
			return `${(count / 1000).toFixed(1)}k`.replace(".0k", "k");
		}
		return count.toString();
	};

	// Nivel 0 -> 1: drill-down (ecran nou, cu buton back)
	const handleCategoryClick = async (category, e) => {
		e.preventDefault();
		e.stopPropagation();

		const currentBreadcrumb = navigationStack[navigationStack.length - 1].breadcrumb || [];
		const children = await fetchChildren(category.id);

		if (children.length > 0) {
			const currentLevel = navigationStack.length - 1;
			setNavigationStack((prev) => [
				...prev,
				{
					categories: children,
					title: categoryName(category),
					parentCategory: category,
					level: currentLevel + 1,
					breadcrumb: [...currentBreadcrumb, { slug: category.slug, name: categoryName(category) }],
				},
			]);
			setExpandedCategories(new Set());
		} else {
			window.location.href = buildCategoryUrl(currentBreadcrumb, category.slug);
		}
	};

	// Nivel 1: rând cu copii -> dropdown inline; fara copii -> navigare directa
	const handleLevel1RowClick = (category, e) => {
		if (category.subChildren && category.subChildren.length > 0) {
			e.preventDefault();
			e.stopPropagation();
			toggleDropdown(category.id);
		} else {
			handleLeafClick(category, e);
		}
	};

	// Navigare directa (subChild din dropdown, sau categorie fara copii)
	const handleLeafClick = (leaf, e, parentCategory) => {
		e.preventDefault();
		e.stopPropagation();

		const currentBreadcrumb = navigationStack[navigationStack.length - 1].breadcrumb || [];
		const breadcrumb = parentCategory
			? [...currentBreadcrumb, { slug: parentCategory.slug, name: categoryName(parentCategory) }]
			: currentBreadcrumb;
		window.location.href = buildCategoryUrl(breadcrumb, leaf.slug);
	};

	const handleBack = () => {
		setNavigationStack((prev) => prev.slice(0, -1));
		setExpandedCategories(new Set());
	};

	const currentView = navigationStack[navigationStack.length - 1] || {
		categories: [],
		title: "Meniu",
		breadcrumb: [],
	};
	const showBackButton = navigationStack.length > 1;
	const displayName = userState && userData?.firstName ? `${userData.firstName} ${userData.lastName || ""}`.trim() : null;

	return (
		<div className='offcanvas offcanvas-start canvas-mb' id='mobileMenu' style={{ top: topOffset }}>
			<style jsx>{`
				.category-item {
					padding: 13px 16px;
					display: flex;
					align-items: center;
					justify-content: space-between;
					cursor: pointer;
					transition: background-color 0.15s ease;
					background-color: transparent;
					flex: 1;
					gap: 12px;
					text-decoration: none;
				}
				.category-item:active {
					background-color: #f5f5f5;
				}
				.category-icon {
					width: 26px;
					height: 26px;
					border-radius: 6px;
					flex-shrink: 0;
					display: flex;
					align-items: center;
					justify-content: center;
					background-color: #f0f4f8;
					color: #0074d9;
					font-size: 14px;
					overflow: hidden;
					position: relative;
				}
				.category-chevron {
					color: #b0b0b0;
					font-size: 18px;
					line-height: 1;
					flex-shrink: 0;
					transition: transform 0.2s ease;
				}
				.subchild-list {
					list-style: none;
					margin: 0;
					padding: 0 0 6px 0;
					background-color: #fafbfc;
				}
				.subchild-item {
					padding: 10px 16px 10px 54px;
					display: block;
					color: #555;
					font-size: 13.5px;
					text-decoration: none;
				}
				.subchild-item:active {
					background-color: #f0f0f0;
				}
				:global(.canvas-mb .mb-canvas-content) {
					padding-top: 0 !important;
					padding-left: 0 !important;
				}
				:global(.canvas-mb .mb-body) {
					padding: 0 !important;
				}
				:global(body.mobile-menu-open #header) {
					z-index: 2500;
				}
			`}</style>
			<div className='mb-canvas-content'>
				<div className='mb-body'>
					{/* Header: profil (nivel 0) sau breadcrumb + back (nivel 1+) */}
					<div
						className='mb-menu-header'
						style={{
							padding: "14px 16px",
							borderBottom: "1px solid #e5e5e5",
							display: "flex",
							alignItems: "center",
							gap: "12px",
							backgroundColor: "#fff",
							position: "sticky",
							top: 0,
							zIndex: 10,
						}}>
						{showBackButton ? (
							<>
								<button
									onClick={handleBack}
									className='btn-back'
									style={{
										background: "none",
										border: "none",
										padding: "4px",
										cursor: "pointer",
										display: "flex",
										alignItems: "center",
										fontSize: "22px",
										color: "#333",
									}}
									aria-label='Înapoi'>
									‹
								</button>
								<span style={{ fontSize: "15px", fontWeight: 600, color: "#262626" }}>
									{currentView.title}
								</span>
							</>
						) : (
							<Link
								href={userState ? "/contul-meu" : "/autentificare"}
								data-bs-dismiss='offcanvas'
								style={{
									display: "flex",
									alignItems: "center",
									gap: "12px",
									textDecoration: "none",
									width: "100%",
								}}>
								<div
									style={{
										width: 44,
										height: 44,
										borderRadius: "50%",
										flexShrink: 0,
										overflow: "hidden",
										position: "relative",
										backgroundColor: displayName ? "#0074d9" : "#f5f5f5",
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
									}}>
									{displayName ? (
										<span style={{ color: "#fff", fontSize: "16px", fontWeight: 600 }}>
											{(userData.firstName?.[0] || "").toUpperCase()}
											{(userData.lastName?.[0] || "").toUpperCase()}
										</span>
									) : (
										<Image
											src='/images/password.webp'
											alt=''
											fill
											sizes='44px'
											style={{ objectFit: "cover" }}
										/>
									)}
								</div>
								<div>
									<div style={{ fontSize: "15px", fontWeight: 600, color: "#262626" }}>
										{displayName || t("autentificare")}
									</div>
									{userState && userData?.admin >= 1 ? (
										<div style={{ fontSize: "12px", color: "#B00020", display: "flex", alignItems: "center", gap: "4px" }}>
											<Shield size={13} /> Administrator
										</div>
									) : userState && userData?.marketplace == 1 ? (
										<div style={{ fontSize: "12px", color: "#7c3aed" }}>Marketplacer</div>
									) : userState && userData?.createdAt ? (
										<div style={{ fontSize: "12px", color: "#999" }}>
											{tenureLabel(userData.createdAt)}
										</div>
									) : (
										!userState && (
											<div style={{ fontSize: "12px", color: "#999" }}>
												{t("contul_meu")}
											</div>
										)
									)}
								</div>
							</Link>
						)}
					</div>

					{/* Lista categorii */}
					{loading ? (
						<div style={{ padding: "40px 20px", textAlign: "center", color: "#666" }}>
							Se încarcă categoriile...
						</div>
					) : (
						<ul
							className='nav-ul-mb'
							style={{
								padding: "0",
								margin: "0",
								listStyle: "none",
							}}>
							{!showBackButton && (
								<>
									<li key='all-products' style={{ borderBottom: "1px solid #f0f0f0" }}>
										<a
											href='/toate-produsele'
											className='category-item'
											onClick={(e) => {
												e.preventDefault();
												window.location.href = "/toate-produsele";
											}}>
											<div style={{ display: "flex", alignItems: "center", flex: 1, gap: "12px" }}>
												<div className='category-icon'>
													<Image
														src='/images/categories/magazin.webp'
														alt=''
														width={26}
														height={26}
														loading='lazy'
														quality={70}
														style={{ objectFit: "contain" }}
													/>
												</div>
												<span style={{ fontSize: "14px", color: "#262626" }}>
													{t("magazin")}
												</span>
											</div>
										</a>
									</li>
									<li key='reduceri' style={{ borderBottom: "1px solid #f0f0f0" }}>
										<a
											href='/reduceri'
											className='category-item'
											onClick={(e) => {
												e.preventDefault();
												window.location.href = "/reduceri";
											}}>
											<div style={{ display: "flex", alignItems: "center", flex: 1, gap: "12px" }}>
												<div className='category-icon'>
													<Image
														src='/images/categories/reduceri.webp'
														alt=''
														width={26}
														height={26}
														loading='lazy'
														quality={70}
														style={{ objectFit: "contain" }}
													/>
												</div>
												<span style={{ fontSize: "14px", color: "#B00020" }}>
													{t("reduceri")}
												</span>
											</div>
										</a>
									</li>
									{userState && userData?.admin >= 1 && (
										<li key='admin-panou' style={{ borderBottom: "1px solid #f0f0f0" }}>
											<a
												href='/admin'
												className='category-item'
												onClick={(e) => {
													e.preventDefault();
													window.location.href = "/admin";
												}}>
												<div style={{ display: "flex", alignItems: "center", flex: 1, gap: "12px" }}>
													<div className='category-icon' style={{ backgroundColor: "#B00020", color: "#fff" }}>
														<Shield size={14} />
													</div>
													<span
														style={{
															fontSize: "14px",
															color: "#fff",
															backgroundColor: "#B00020",
															padding: "4px 12px",
															borderRadius: 14,
															fontWeight: 600,
														}}>
														{t("admin_panou")}
													</span>
												</div>
											</a>
										</li>
									)}
								</>
							)}

							{currentView.categories.map((category) => {
								const hasSubChildren = category.subChildren && category.subChildren.length > 0;
								const isExpanded = expandedCategories.has(category.id);
								const productCount = formatProductCount(category.productCount);
								const isLevel0 = !showBackButton;

								return (
									<li key={category.id} style={{ borderBottom: "1px solid #f0f0f0" }}>
										<div
											className='category-item'
											onClick={(e) =>
												isLevel0 ? handleCategoryClick(category, e) : handleLevel1RowClick(category, e)
											}>
											<div style={{ display: "flex", alignItems: "center", flex: 1, gap: "12px", minWidth: 0 }}>
												<div className='category-icon'>
													{category.image ? (
														<Image
															src={category.image}
															alt=''
															width={26}
															height={26}
															loading='lazy'
															quality={60}
															style={{ objectFit: "cover" }}
														/>
													) : (
														<i className='icon icon-bag' style={{ fontSize: "13px" }} />
													)}
												</div>
												<span
													style={{
														fontSize: "14px",
														color: "#262626",
														overflow: "hidden",
														textOverflow: "ellipsis",
														whiteSpace: "nowrap",
													}}>
													{categoryName(category)}
												</span>
												{productCount && (
													<span
														style={{
															backgroundColor: "#f0f0f0",
															color: "#666",
															fontSize: "11px",
															padding: "2px 7px",
															borderRadius: "12px",
															fontWeight: "500",
															flexShrink: 0,
														}}>
														{productCount}
													</span>
												)}
											</div>

											<span
												className='category-chevron'
												style={{
													transform: !isLevel0 && hasSubChildren && isExpanded ? "rotate(90deg)" : "none",
												}}>
												›
											</span>
										</div>

										{/* Dropdown inline pt subChildren (doar nivel 1) */}
										{!isLevel0 && hasSubChildren && isExpanded && (
											<ul className='subchild-list'>
												{category.subChildren.map((subChild) => {
													const subChildCount = formatProductCount(subChild.productCount);
													return (
														<li key={subChild.id}>
															<a
																href='#'
																className='subchild-item'
																style={{ display: "flex", alignItems: "center", gap: "8px" }}
																onClick={(e) => handleLeafClick(subChild, e, category)}>
																<span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
																	{categoryName(subChild)}
																</span>
																{subChildCount && (
																	<span
																		style={{
																			backgroundColor: "#f0f0f0",
																			color: "#666",
																			fontSize: "11px",
																			padding: "2px 7px",
																			borderRadius: "12px",
																			fontWeight: "500",
																			flexShrink: 0,
																		}}>
																		{subChildCount}
																	</span>
																)}
															</a>
														</li>
													);
												})}
											</ul>
										)}
									</li>
								);
							})}
						</ul>
					)}

					{/* Link "Vezi toate produsele" din categoria parinte */}
					{showBackButton && currentView.parentCategory && (
						<div
							style={{
								padding: "16px",
								borderTop: "2px solid #e5e5e5",
								backgroundColor: "#fafafa",
							}}>
							<Link
								href={buildCategoryUrl(
									currentView.breadcrumb.slice(0, -1),
									currentView.parentCategory.slug
								)}
								style={{
									display: "block",
									textAlign: "center",
									padding: "12px 16px",
									backgroundColor: "#0066cc",
									color: "#fff",
									textDecoration: "none",
									borderRadius: "4px",
									fontSize: "14px",
									fontWeight: "600",
								}}>
								Vezi toate din {categoryName(currentView.parentCategory)}
							</Link>
						</div>
					)}

					<div style={{ height: "80px" }} />
				</div>
			</div>
		</div>
	);
}
